import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  ClockIcon,
  CrossIcon,
  ExternalLink,
  DocumentIcon,
  Pencil,
  Plus,
  Warning,
} from '../../../../../assets/icons';
import { dateWordShort, parseCalendarDate } from '../../../../../helper/helper';
import UseContext from '../../../../../hooks/useContext';
import usefetchdata from '../../../../../hooks/usefetchdata';
import ApiDatabase from '../../../../../server';
import Badge from '../../../../badge/badge';
import BadgeSquare from '../../../../badge/badgeSquare';
import Button from '../../../../button';
import CopyToClipboardLarge from '../../../../copyToClipboard/copyToClipboardLarge';
import Divider from '../../../../divider/divider';
import Experiences from '../../../../experiences/experiences';
import IdentityFileItem from '../../../../identityFileItem/IdentityFileItem';
import Licences from '../../../../licences/licences';
import UseModal from '../../../../modal/useModal';
import Tooltip from '../../../../tooltips/Tooltips';
import Trainings from '../../../../trainings/trainings';
import {
  EXCLUSION_VIVIER_ID,
  buildSelectedVivierStatusMap,
  toUserIdString,
} from '../../../../../utils/vivierUserPatch';

const PreviewTempWorker = React.forwardRef(({ tempWorkerInfos }, ref) => {
  const {
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    setAdminFileName,
    setInfoVivier,
    setUserVivierToArchive,
    userVivierToArchiveUpdated,
    setUserVivierToArchiveUpdated,
  } = UseContext();
  const { userData } = usefetchdata();
  const { toggle } = UseModal();
  const [isVivierOpen, setIsVivierOpen] = useState(true);
  const [isIdentityFilesOpen, setIsIdentityFilesOpen] = useState(false);
  const [isOtherDocumentsOpen, setIsOtherDocumentsOpen] = useState(false);
  const [isTrainingGuidesOpen, setIsTrainingGuidesOpen] = useState(false);

  const [selectedVivierStatus, setSelectedVivierStatus] = useState({});
  const [isToastVisible, setIsToastVisible] = useState(false);
  const vivierRef = useRef(null);
  const [isIdentityFileOpen, setIsIdentityFileOpen] = useState({
    identity: false,
    selfie: false,
    visa: false,
    card_vitality: false,
  });
  const token = localStorage.getItem('xsrfToken');

  const kanbanColumns = [
    { name: 'wish', title: 'Souhait', icon: '🧞' },
    { name: 'test', title: 'Test', icon: '⏳' },
    { name: 'valid', title: 'Validé', icon: '✅' },
    { name: 'refused', title: 'Refusé', icon: '❌' },
    { name: 'archive', title: 'Archivé', icon: '📦' },
  ];

  const openDocument = (type, type2) => {
    setAdminType(type);
    setAdminUserId(tempWorkerInfos?.tempWorker?.id_user);
    setAdminTempWorkerId(tempWorkerInfos?.tempWorker?._id);

    if (type2 === 'view') {
      toggle('ad_file_view');
    } else {
      if (type === 'identity') toggle('ad_file_accept_identity');
      else if (type === 'visa') toggle('ad_file_accept_visa');
      else if (type === 'card_vitality') toggle('ad_file_accept_card_vitality');
      else if (type === 'proof_of_address') toggle('ad_file_accept_simple');
      else if (type === 'accommodation_of_address')
        toggle('ad_file_accept_simple');
      else if (type === 'hostIdentity') toggle('ad_file_accept_simple');
      else toggle('ad_file_view');
    }
  };

  const addVivierSelected = (e, idUser) => {
    e.stopPropagation();
    setInfoVivier({ listUsers: [idUser] });
    toggle('vivier_add');
  };

  const handleChangeVivierStatus = (idVivier, vivierStatus) => {
    if (idVivier && vivierStatus) {
      if (vivierStatus === 'archive') {
        setUserVivierToArchive({
          idUsers: [tempWorkerInfos?.user?._id],
          idVivier,
          vivierStatus: vivierStatus,
        });

        toggle('vivier_user_archive');
      } else {
        ApiDatabase.updUserVivierPreferenceStatus(
          {
            token,
            idUsers: [tempWorkerInfos?.user?._id],
            idVivier,
            vivierStatus: vivierStatus,
          },
          (data) => {
            if (!isToastVisible) {
              setIsToastVisible(true);
              toast.success(`L'utilisateur a été déplacé.`);
            }

            setSelectedVivierStatus((prevData) => ({
              ...prevData,
              [toUserIdString(idVivier)]: vivierStatus,
            }));
          },
          (err) => {
            console.log('err', err);
          }
        );
      }
    }
  };

  useEffect(() => {
    if (
      userVivierToArchiveUpdated.idUsers &&
      userVivierToArchiveUpdated.idVivier &&
      userVivierToArchiveUpdated.vivierStatus
    ) {
      for (let i = 0; i < userVivierToArchiveUpdated.idUsers.length; i++) {
        if (
          tempWorkerInfos?.user?._id === userVivierToArchiveUpdated.idUsers[i]
        ) {
          setSelectedVivierStatus((prevData) => ({
            ...prevData,
            [toUserIdString(userVivierToArchiveUpdated.idVivier)]:
              userVivierToArchiveUpdated.vivierStatus,
          }));
        }
      }
      setUserVivierToArchiveUpdated({});
    }
  }, [userVivierToArchiveUpdated]);

  useEffect(() => {
    if (!tempWorkerInfos?.viviers?.length || !tempWorkerInfos?.user?._id) return;

    setSelectedVivierStatus((prevData) => ({
      ...prevData,
      ...buildSelectedVivierStatusMap(
        tempWorkerInfos.viviers,
        tempWorkerInfos.user._id
      ),
    }));
  }, [tempWorkerInfos?.viviers, tempWorkerInfos?.user?._id]);

  useEffect(() => {
    if (isToastVisible) {
      const timeoutId = setTimeout(() => {
        setIsToastVisible(false);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [isToastVisible]);

  const excludeVivier = (idVivier) => {
    setInfoVivier({
      idVivier: idVivier,
      nameUser:
        tempWorkerInfos?.user?.firstname +
        ' ' +
        tempWorkerInfos?.user?.lastname,
      listUsers: [tempWorkerInfos?.user?._id],
    });
    toggle('vivier_delete_candidate');
  };

  const changeLocalisation = (idUser) => {
    setInfoVivier({ listUsers: [idUser] });
    toggle('localisation_preferences');
  };

  const getAvailabilityText = () => {
    const availability = tempWorkerInfos?.tempWorker?.availability;

    if (!availability) {
      return 'Non renseigné';
    }

    const { is_available, date_start, date_end } = availability;

    if (is_available === false) {
      if (!date_start && !date_end) {
        return 'Non disponible';
      } else if (date_start && !date_end) {
        return "Non disponible jusqu'au " + dateWordShort(date_start);
      } else if (!date_start && date_end) {
        return (
          'Non disponible à partir du ' + dateWordShort(date_end)
        );
      } else {
        return (
          'Disponible du ' +
          dateWordShort(date_start) +
          ' au ' +
          dateWordShort(date_end)
        );
      }
    } else if (is_available === true) {
      if (!date_start && !date_end) {
        return 'Disponibilité complète';
      } else if (date_start && !date_end) {
        return 'Disponible à partir du ' + dateWordShort(date_start);
      } else if (!date_start && date_end) {
        return "Disponible jusqu'au " + dateWordShort(date_end);
      } else {
        return (
          'Disponible du ' +
          dateWordShort(date_start) +
          ' au ' +
          dateWordShort(date_end)
        );
      }
    } else {
      return 'Non renseigné';
    }
  };

  const getAvailabilityStatus = (unavailableDays) => {
    const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
    return days.map((day, index) => {
      return {
        day,
        status: unavailableDays.includes(index) ? false : true,
      };
    });
  };

  useImperativeHandle(ref, () => ({
    scrollToVivier: () => {
      setIsVivierOpen(true);
      if (vivierRef.current) {
        vivierRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
  }));

  // Vérifier si le profil est complet
  const isProfileComplete = () => {
    const identity = tempWorkerInfos.tempWorker?.identity;
    const selfie = tempWorkerInfos.tempWorker?.selfie;
    const cardVitality = tempWorkerInfos.tempWorker?.card_vitality;
    const proofOfAddress = tempWorkerInfos.tempWorker?.proof_of_address;

    // Un fichier est considéré comme validé si validated === '1' ou '2' ou '5'
    const isIdentityValidated = identity?.validated === '1' || identity?.validated === '2' || identity?.validated === '5';
    const isSelfieValidated = selfie?.validated === '1' || selfie?.validated === '2' || selfie?.validated === '5';
    const isCardVitalityValidated = cardVitality?.validated === '1' || cardVitality?.validated === '2' || cardVitality?.validated === '5';
    const isProofOfAddressValidated = proofOfAddress?.validated === '1' || proofOfAddress?.validated === '2' || proofOfAddress?.validated === '5';

    const hostIdentity = tempWorkerInfos.tempWorker?.hostIdentity;
    const hasHostIdentity = hostIdentity?.validated && hostIdentity?.validated !== '0';
    const isHostIdentityValidated = !hasHostIdentity || hostIdentity?.validated === '2' || hostIdentity?.validated === '5';

    return isIdentityValidated && isSelfieValidated && isCardVitalityValidated && isProofOfAddressValidated && isHostIdentityValidated;
  };

  // Pour l'Identity
  const identityData = tempWorkerInfos.tempWorker?.identity;
  const identityTitle =
    identityData?.type === 'Passeport' ? 'Passeport' : "Carte d'identité";

  const identityValidatedType =
    identityData?.validated === '2' ? '5' : identityData?.validated;

  const identityDetails = [
    {
      label: "Numéro d'identité",
      value: identityData?.number_identity,
    },
    {
      label: 'Ville de naissance',
      value: identityData?.birthplace,
    },
    {
      label: 'Validité',
      value: `${
        identityData?.start_validity
          ? dateWordShort(identityData.start_validity)
          : 'NaN'
      } - ${
        identityData?.end_validity
          ? dateWordShort(identityData.end_validity)
          : 'NaN'
      }`,
    },
  ];

  // Pour le selfie
  const selfieData = tempWorkerInfos.tempWorker?.selfie;
  const selfieTitle = 'Selfie';

  const selfieValidatedType =
    selfieData?.validated === '2' ? '5' : selfieData?.validated;

  const selfieDetails = [];

  // Pour le Visa
  const visaData = tempWorkerInfos.tempWorker?.visa;
  const visaTitle = visaData?.type === 'Visa' ? 'Visa' : 'Titre de séjour';

  const visaValidatedType =
    visaData?.validated === '2' ? '5' : visaData?.validated;

  const visaDetails = [
    {
      label: 'Numéro',
      value: visaData?.number_visa,
    },
    {
      label: 'Validité',
      value: `${
        visaData?.start_validity
          ? dateWordShort(visaData.start_validity)
          : 'NaN'
      } - ${
        visaData?.end_validity ? dateWordShort(visaData.end_validity) : 'NaN'
      }`,
    },
  ];

  // Pour la Carte Vitale
  const cardVitalityData = tempWorkerInfos.tempWorker?.card_vitality;
  const cardVitalityTitle = 'Carte vitale';

  const cardVitalityValidatedType =
    cardVitalityData?.validated === '2' ? '5' : cardVitalityData?.validated;

  const cardVitalityDetails = [
    {
      label: 'Numéro',
      value: cardVitalityData?.social_security,
    },
  ];

  // Pour le Justificatif d'adresse
  const proofOfAddressData = tempWorkerInfos.tempWorker?.proof_of_address;
  const proofOfAddressTitle = "Justificatif d'adresse";

  const proofOfAddressValidatedType =
    proofOfAddressData?.validated === '2' ? '5' : proofOfAddressData?.validated;

  // Pour l'Attestation d'hébergement
  const accommodationData =
    tempWorkerInfos.tempWorker?.accommodation_of_address;
  const accommodationTitle = "Attestation d'hébergement";

  const accommodationValidatedType =
    accommodationData?.validated === '2' ? '5' : accommodationData?.validated;

  // Pour la Pièce d'identité de l'hôte
  const hostIdentityData = tempWorkerInfos.tempWorker?.hostIdentity;
  const hostIdentityTitle = "Pièce d'identité de l'hôte";

  const hostIdentityValidatedType =
    hostIdentityData?.validated === '2' ? '5' : hostIdentityData?.validated;

  return (
    <>
      <div className={'border border-gray-200 rounded-lg overflow-hidden'}>
        <div className={'grid grid-cols-2 divide-x divide-y divide-gray-200'}>
          <div className={'p-4 flex justify-between items-center'}>
            <CopyToClipboardLarge
              libelle={'Adresse e-mail'}
              text={tempWorkerInfos?.user.email}
            />
          </div>
          <div className={'p-4'}>
            <CopyToClipboardLarge
              libelle={'Numéro'}
              text={tempWorkerInfos?.user.phone}
            />
          </div>
          <div className={'p-4'}>
            <div className={'flex flex-col'}>
              <div className={''}>Date de naissance</div>
              <div className={'my-auto truncate text-gray-500 text-sm'}>
                {tempWorkerInfos?.user?.birthday &&
                !tempWorkerInfos.user.birthday.includes('NaN')
                  ? tempWorkerInfos.user.birthday
                  : 'Non renseignée'}
              </div>
            </div>
          </div>
          <div className={'p-4'}>
            <div className={'flex justify-between gap-2 items-start'}>
              <div className={'flex-1'}>
                <div className={'flex flex-col gap-1'}>
                  <div className={''}>Localisation</div>
                  {tempWorkerInfos?.tempWorker?.preferences_department?.city ? (
                    <>
                      <div className={'text-gray-900 text-sm'}>
                        {tempWorkerInfos?.tempWorker?.preferences_department?.city}
                        {tempWorkerInfos?.tempWorker?.preferences_department?.department && 
                          `, ${tempWorkerInfos?.tempWorker?.preferences_department?.department}`
                        }
                      </div>
                      {tempWorkerInfos?.tempWorker?.preferences_department?.full_address && (
                        <div className={'text-gray-500 text-sm'}>
                          {tempWorkerInfos?.tempWorker?.preferences_department?.full_address}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={'text-gray-500 text-sm'}>Non renseigné</div>
                  )}
                </div>
              </div>
              <div
                className={
                  'border border-gray-300 rounded-md w-8 h-8 flex justify-center items-center cursor-pointer hover:bg-gray-50 transition-colors flex-shrink-0'
                }
                onClick={() => changeLocalisation(tempWorkerInfos?.user._id)}
              >
                <Pencil wh={18} color={'#374151'} />
              </div>
            </div>
          </div>
          <div className={'p-4'}>
            <div className={'flex justify-between gap-2 items-start'}>
              <div className={'flex-1'}>
                <div className={'flex flex-col'}>
                  <div className={''}>Nationalité</div>
                  <div className={'my-auto truncate text-gray-500 text-sm'}>
                    {tempWorkerInfos?.tempWorker?.identity?.nationality
                      ? tempWorkerInfos?.tempWorker?.identity?.nationality
                          .NationaliteF
                      : 'Non renseigné'}
                  </div>
                </div>
              </div>
              <div className={'flex gap-1 items-center flex-shrink-0'}>
                {tempWorkerInfos?.tempWorker?.visa?.end_validity && (
                  <>
                    {parseCalendarDate(
                      tempWorkerInfos?.tempWorker?.visa?.end_validity
                    ).getTime() < new Date().getTime() && (
                      <Tooltip
                        text={`Date de fin de visa : ${dateWordShort(
                          tempWorkerInfos?.tempWorker?.visa?.end_validity
                        )}`}
                        placement="top"
                      >
                        <div>
                          <ClockIcon wh={20} color={'#DC2626'} />
                        </div>
                      </Tooltip>
                    )}
                  </>
                )}
                {tempWorkerInfos?.tempWorker?.identity?.nationality
                  ?.UnionEuropéenne === 0 && (
                  <Tooltip text={`Pays hors UE`} placement="top">
                    <div>
                      <Warning wh={20} color={'#D77A1E'} />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
          <div className={'p-4'}>
            {tempWorkerInfos?.tempWorker?.bank?.iban ? (
              <CopyToClipboardLarge
                libelle={'IBAN'}
                text={tempWorkerInfos?.tempWorker?.bank?.iban.replace(/\s/g, '')}
              />
            ) : (
              <>
                <div className={'flex flex-col'}>
                  <div className={''}>IBAN</div>
                  <div className={'my-auto truncate text-gray-500 text-sm'}>Non renseigné</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={'border border-gray-200 rounded-lg p-3 col-span-full'}>
        <div className={'flex justify-between items-center flex-wrap'}>
          <div className={'flex gap-1 items-center'}>
            <div>
              <Calendar wh={20} color={'#374151'} />
            </div>
            <div>{getAvailabilityText()}</div>
          </div>
          <div className={'flex gap-1 items-center'}>
            {getAvailabilityStatus(
              tempWorkerInfos?.tempWorker?.unavailable_days ?? []
            ).map((day, index) => (
              <div
                key={index}
                className={`rounded w-[24px] h-[24px] flex justify-center items-center cursor-pointer ${
                  day.status
                    ? 'text-green-500 bg-green-50'
                    : 'text-gray-500 bg-gray-100'
                }`}
              >
                <Tooltip
                  text={day.status ? 'Disponible' : 'Non disponible'}
                  placement="top"
                >
                  <div>{day.day}</div>
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={'border border-gray-200 rounded-lg p-3 flex flex-col gap-6'}>
        <div
          className={'flex justify-between select-none cursor-pointer'}
          onClick={() => setIsIdentityFilesOpen(!isIdentityFilesOpen)}
        >
          <div className={'my-auto'}>Fichiers d'identité</div>
          <div className={'my-auto flex items-center gap-2'}>
            <Badge type={isProfileComplete() ? 'VALID' : 'ALERT'}>
              {isProfileComplete() ? 'Complet' : 'Incomplet'}
            </Badge>
            <div
              className={
                'flex cursor-pointer border border-gray-300 rounded-md w-8 h-8 items-center justify-center'
              }
            >
              {isIdentityFilesOpen ? (
                <ChevronDown wh={20} color={'#374151'} />
              ) : (
                <ChevronRight wh={24} color={'#374151'} />
              )}
            </div>
          </div>
        </div>
        {isIdentityFilesOpen && (
          <div className={'grid gap-2 grid-cols-1 md:grid-cols-2'}>
            {/* Identity */}
            <IdentityFileItem
              title={identityTitle}
              isOpen={isIdentityFileOpen.identity}
              onToggle={() => {
                setIsIdentityFileOpen((prevData) => ({
                  ...prevData,
                  identity: !prevData.identity,
                }));
              }}
              openDocument={openDocument}
              id="identity"
              validatedType={identityValidatedType}
              details={identityDetails}
            />
            {/* Identity */}
            <IdentityFileItem
              title={selfieTitle}
              isOpen={isIdentityFileOpen.selfie}
              onToggle={() => {
                setIsIdentityFileOpen((prevData) => ({
                  ...prevData,
                  selfie: !prevData.selfie,
                }));
              }}
              openDocument={openDocument}
              id="selfie"
              validatedType={selfieValidatedType}
              details={selfieDetails}
            />
            {/* Visa */}
            {tempWorkerInfos.tempWorker.visa?.type && (
              <IdentityFileItem
                title={visaTitle}
                isOpen={isIdentityFileOpen.visa}
                onToggle={() => {
                  setIsIdentityFileOpen((prevData) => ({
                    ...prevData,
                    visa: !prevData.visa,
                  }));
                }}
                openDocument={openDocument}
                id="visa"
                validatedType={visaValidatedType}
                details={visaDetails}
              />
            )}
            {/* Carte Vitale */}
            <IdentityFileItem
              title={cardVitalityTitle}
              isOpen={isIdentityFileOpen.card_vitality}
              onToggle={() => {
                setIsIdentityFileOpen((prevData) => ({
                  ...prevData,
                  card_vitality: !prevData.card_vitality,
                }));
              }}
              openDocument={openDocument}
              id="card_vitality"
              validatedType={cardVitalityValidatedType}
              details={cardVitalityDetails}
            />
            {/* Justificatif d'adresse */}
            <IdentityFileItem
              title={proofOfAddressTitle}
              openDocument={openDocument}
              id="proof_of_address"
              validatedType={proofOfAddressValidatedType}
              details={[]}
            />
            {/* Attestation d'hébergement */}
            {tempWorkerInfos.tempWorker?.accommodation_of_address?.validated !==
              '0' && (
              <IdentityFileItem
                title={accommodationTitle}
                openDocument={openDocument}
                id="accommodation_of_address"
                validatedType={accommodationValidatedType}
                details={[]}
              />
            )}
            {/* Pièce d'identité de l'hôte */}
            {tempWorkerInfos.tempWorker?.hostIdentity?.validated &&
              tempWorkerInfos.tempWorker?.hostIdentity?.validated !== '0' && (
              <IdentityFileItem
                title={hostIdentityTitle}
                openDocument={openDocument}
                id="hostIdentity"
                validatedType={hostIdentityValidatedType}
                details={[]}
              />
            )}
          </div>
        )}
      </div>
      <div className={'border rounded p-3 flex flex-col gap-6'}>
        <div
          className={'flex justify-between select-none cursor-pointer'}
          onClick={() => setIsOtherDocumentsOpen(!isOtherDocumentsOpen)}
        >
          <div className={'my-auto'}>Autres Documents</div>
          <div className={'my-auto flex items-center gap-2'}>
            <div
              className={
                'border rounded bg-gray-100 w-5 h-5 flex justify-center items-center text-sm'
              }
            >
              {tempWorkerInfos?.tempWorker?.otherDocuments?.length || 0}
            </div>
            <div
              className={
                'flex cursor-pointer border rounded w-8 h-8 items-center justify-center'
              }
            >
              {isOtherDocumentsOpen ? (
                <ChevronDown wh={20} color={'#374151'} />
              ) : (
                <ChevronRight wh={24} color={'#374151'} />
              )}
            </div>
          </div>
        </div>
        {isOtherDocumentsOpen && (
          <div className={'flex flex-col gap-2'}>
            {tempWorkerInfos.tempWorker.otherDocuments?.length > 0 ? (
              tempWorkerInfos.tempWorker.otherDocuments.map((doc) => (
                <div
                  key={doc._id}
                  className={'p-2 border rounded h-fit flex justify-between items-center gap-2'}
                >
                  <div className={'flex flex-col'}>
                    <div className={'ft-sm font-medium'}>{doc.documentType}</div>
                    <div className={'ft-xs text-gray-500'}>
                      {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div
                    className={'cursor-pointer'}
                    onClick={() => {
                      setAdminType('otherDocument');
                      setAdminFileName(doc.fileName);
                      setAdminUserId(tempWorkerInfos?.tempWorker?.id_user);
                      setAdminTempWorkerId(tempWorkerInfos?.tempWorker?._id);
                      toggle('ad_file_view');
                    }}
                  >
                    <ExternalLink wh={18} color={'#235fe6'} />
                  </div>
                </div>
              ))
            ) : (
              <div className={'ft-sm text-gray-500'}>Aucun document</div>
            )}
          </div>
        )}
      </div>
      <div className={'border rounded p-3 flex flex-col gap-6'}>
        <div
          className={'flex justify-between select-none cursor-pointer'}
          onClick={() => setIsTrainingGuidesOpen(!isTrainingGuidesOpen)}
        >
          <div className={'my-auto'}>Guides & Formations</div>
          <div className={'my-auto'}>
            <div
              className={
                'flex cursor-pointer border rounded w-8 h-8 items-center justify-center'
              }
            >
              {isTrainingGuidesOpen ? (
                <ChevronDown wh={20} color={'#374151'} />
              ) : (
                <ChevronRight wh={24} color={'#374151'} />
              )}
            </div>
          </div>
        </div>
        {isTrainingGuidesOpen && (
          <div className={'flex flex-col gap-6'}>
            <div className={'flex justify-between gap-2'}>
              <div className={'my-auto'}>Guide de bienvenue</div>
              <div className={'my-auto'}>
                <BadgeSquare
                  type={
                    tempWorkerInfos?.tempWorker?.welcome_guide?.is_read ? 6 : 7
                  }
                />
              </div>
            </div>
            <div className={'flex justify-between gap-2'}>
              <div className={'my-auto'}>Livret de sécurité</div>
              <div className={'my-auto'}>
                <BadgeSquare
                  type={
                    tempWorkerInfos?.tempWorker?.safety_booklet?.is_read ? 6 : 7
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div ref={vivierRef} className={'border rounded p-3 flex flex-col gap-6'}>
        <div
          className={'flex justify-between select-none cursor-pointer'}
          {...(Array.isArray(tempWorkerInfos.viviers) && tempWorkerInfos.viviers.length > 0
            ? { onClick: () => setIsVivierOpen(!isVivierOpen) }
            : {})}
        >
          <div className={'my-auto'}>Vivier</div>
          <div className={'my-auto'}>
            {!Array.isArray(tempWorkerInfos.viviers) || tempWorkerInfos.viviers.length === 0 ? (
              <Button
                color={'SECONDARY'}
                size={'SM'}
                onClick={(e) =>
                  addVivierSelected(e, tempWorkerInfos?.user?._id)
                }
              >
                <div className={'flex gap-1 items-center'}>
                  <div className={''}>
                    <Plus wh={20} color={'#374151'} />
                  </div>
                  <div className={''}>Ajouter</div>
                </div>
              </Button>
            ) : (
              <>
                <div className={'flex gap-2 items-center'}>
                  <div className={'text-gray-500 text-sm'}>
                    {Array.isArray(tempWorkerInfos.viviers) && tempWorkerInfos.viviers.length === 1
                      ? '1 vivier'
                      : Array.isArray(tempWorkerInfos.viviers) ? tempWorkerInfos.viviers.length + ' viviers' : '0 vivier'}
                  </div>
                  <div
                    className={
                      'flex cursor-pointer border border-gray-300 rounded-md w-8 h-8 items-center justify-center'
                    }
                    onClick={(e) =>
                      addVivierSelected(e, tempWorkerInfos?.user?._id)
                    }
                  >
                    <Plus wh={20} color={'#374151'} />
                  </div>
                  <div
                    className={
                      'flex cursor-pointer border border-gray-300 rounded-md w-8 h-8 items-center justify-center'
                    }
                  >
                    {isVivierOpen ? (
                      <ChevronDown wh={20} color={'#374151'} />
                    ) : (
                      <ChevronRight wh={20} color={'#374151'} />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {isVivierOpen &&
          Array.isArray(tempWorkerInfos.viviers) &&
          tempWorkerInfos.viviers.length > 0 && (
          <div className={'flex flex-col gap-6'}>
            {Array.isArray(tempWorkerInfos.viviers) && tempWorkerInfos.viviers.map((vivier, index) => {
              const vivierId = toUserIdString(vivier._id);
              const vivierStatus = selectedVivierStatus[vivierId];

              return (
              <div
                key={index}
                className={'flex justify-between gap-2 items-center'}
              >
                <div className={''}>{vivier.name}</div>
                <div className={'flex gap-2 items-center'}>
                  <div className={''}>
                    {vivierStatus === 'archive' ? (
                      <div
                        className={
                          'w-full px-2 py-1 ft-b border rounded-lg text-gray-900 bg-gray-100 flex items-center'
                        }
                      >
                        {kanbanColumns.map((kanban, kanbanIndex) => {
                          if (vivierStatus === kanban.name) {
                            return (
                              <div
                                key={kanbanIndex}
                                className={'flex gap-1 items-center'}
                              >
                                <div className={'my-auto'}>{kanban.icon}</div>
                                <div className={'my-auto'}>{kanban.title}</div>
                              </div>
                            );
                          }
                        })}
                      </div>
                    ) : (
                      <>
                        {vivierId !== EXCLUSION_VIVIER_ID && (
                          <select
                            className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-gray-900"
                            value={vivierStatus || ''}
                            onChange={(e) =>
                              handleChangeVivierStatus(
                                vivier._id,
                                e.target.value
                              )
                            }
                          >
                            {kanbanColumns.map((kanban, kanbanIndex) => (
                              <option key={kanbanIndex} value={kanban.name}>
                                {kanban.icon} - {kanban.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    )}
                  </div>
                  <div
                    className={'cursor-pointer'}
                    onClick={() => excludeVivier(vivier._id)}
                  >
                    <CrossIcon wh={20} color={'#374151'} />
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>
      <div className={'bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'}>
        {/* Langues */}
        <div className={'p-5 border-b border-gray-100'}>
          <div className={'flex justify-between gap-2 items-center mb-4'}>
            <div className={'my-auto'}>Langues</div>
            <BadgeSquare type={'PRIMARY'}>
              {tempWorkerInfos?.tempWorker?.language?.length || 0}
            </BadgeSquare>
          </div>
          {tempWorkerInfos?.tempWorker?.language?.length > 0 ? (
            <div className={'flex flex-col gap-3'}>
              {tempWorkerInfos?.tempWorker?.language?.map((language, index) => (
                <div key={index} className={'flex gap-3 items-center'}>
                  <div
                    className={
                      'w-10 h-10 min-w-10 min-h-10 flex justify-center items-center bg-yellow-50 rounded-lg'
                    }
                  >
                    <span className={'text-yellow-600 font-semibold text-sm'}>Aa</span>
                  </div>
                  <div className={'text-gray-700'}>{language}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={'text-gray-400 text-sm'}>
              Aucune langue renseignée
            </div>
          )}
        </div>

        {/* Guide */}
        <div className={'p-5 border-b border-gray-100'}>
          <div className={'flex justify-between gap-2 items-center mb-4'}>
            <div className={'my-auto'}>Guide</div>
            <BadgeSquare type={'PRIMARY'}>3</BadgeSquare>
          </div>
          <div className={'flex flex-col gap-3'}>
            <div className={'flex gap-3 items-center'}>
              <div
                className={
                  'w-10 h-10 min-w-10 min-h-10 flex justify-center items-center bg-blue-50 rounded-lg'
                }
              >
                <DocumentIcon wh={20} color={'#3B82F6'} />
              </div>
              <div className={'flex-1 flex justify-between items-center'}>
                <div className={'text-gray-700'}>Guide de bienvenue</div>
                <BadgeSquare
                  type={
                    tempWorkerInfos?.tempWorker?.welcome_guide?.is_read ? 6 : 7
                  }
                />
              </div>
            </div>
            <div className={'flex gap-3 items-center'}>
              <div
                className={
                  'w-10 h-10 min-w-10 min-h-10 flex justify-center items-center bg-blue-50 rounded-lg'
                }
              >
                <DocumentIcon wh={20} color={'#3B82F6'} />
              </div>
              <div className={'flex-1 flex justify-between items-center'}>
                <div className={'text-gray-700'}>Livret de sécurité</div>
                <BadgeSquare
                  type={
                    tempWorkerInfos?.tempWorker?.safety_booklet?.is_read ? 6 : 7
                  }
                />
              </div>
            </div>
            <div className={'flex gap-3 items-center'}>
              <div
                className={
                  'w-10 h-10 min-w-10 min-h-10 flex justify-center items-center bg-blue-50 rounded-lg'
                }
              >
                <DocumentIcon wh={20} color={'#3B82F6'} />
              </div>
              <div className={'flex-1 flex justify-between items-center'}>
                <div className={'text-gray-700'}>Guide intérimaire santé</div>
                <BadgeSquare
                  type={
                    tempWorkerInfos?.tempWorker?.health_guide?.is_read ? 6 : 7
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Formations */}
        <div className={'p-5 border-b border-gray-100'}>
          <div className={'flex justify-between gap-2 items-center mb-4'}>
            <div className={'my-auto'}>Formations</div>
            <BadgeSquare type={'PRIMARY'}>
              {tempWorkerInfos?.tempWorker?.formations?.length || 0}
            </BadgeSquare>
          </div>
          {tempWorkerInfos?.tempWorker?.formations?.length > 0 ? (
            <div className={'flex flex-col gap-3 text-sm'}>
              {tempWorkerInfos?.tempWorker?.formations && (
                <Trainings formations={tempWorkerInfos.tempWorker.formations} />
              )}
            </div>
          ) : (
            <div className={'text-gray-400 text-sm'}>
              Aucune formation renseignée
            </div>
          )}
        </div>

        {/* Permis */}
        <div className={'p-5 border-b border-gray-100'}>
          <div className={'flex justify-between gap-2 items-center mb-4'}>
            <div className={'my-auto'}>Permis</div>
            <BadgeSquare type={'PRIMARY'}>
              {tempWorkerInfos?.tempWorker?.licences?.length || 0}
            </BadgeSquare>
          </div>
          {tempWorkerInfos?.tempWorker?.licences?.length > 0 ? (
            <div className={'flex flex-col gap-3 text-sm'}>
              {tempWorkerInfos?.tempWorker?.licences && (
                <Licences
                  licences={tempWorkerInfos.tempWorker.licences}
                  tempWorkerInfos={tempWorkerInfos}
                />
              )}
            </div>
          ) : (
            <div className={'text-gray-400 text-sm'}>
              Aucun permis renseigné
            </div>
          )}
        </div>

        {/* Expériences */}
        <div className={'p-5'}>
          <div className={'flex justify-between gap-2 items-center mb-4'}>
            <div className={'my-auto'}>Expériences</div>
            <BadgeSquare type={'PRIMARY'}>
              {tempWorkerInfos?.tempWorker?.experiences?.length || 0}
            </BadgeSquare>
          </div>
          {tempWorkerInfos?.tempWorker?.experiences?.length > 0 ? (
            <div className={'flex flex-col gap-3 text-sm'}>
              {tempWorkerInfos?.tempWorker?.experiences && (
                <Experiences
                  experiences={tempWorkerInfos.tempWorker.experiences}
                />
              )}
            </div>
          ) : (
            <div className={'text-gray-400 text-sm'}>
              Aucune expérience renseignée
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export default PreviewTempWorker;
